import React, { useState, useEffect } from 'react';
import { sinistroService } from '../../services';

interface SinistroCardProps {
  className?: string;
}

const SinistroCard: React.FC<SinistroCardProps> = ({ className = '' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sinistros, setSinistros] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  // Carregar sinistros da API
  const loadSinistros = async () => {
    try {
      setLoading(true);
      const response = await sinistroService.list({
        page: 1,
        limit: 10,
        sortBy: 'data',
        sortOrder: 'desc'
      });
      
      const data = response?.data || response || [];
      if (Array.isArray(data)) {
        setSinistros(data);
      } else if (data.items && Array.isArray(data.items)) {
        setSinistros(data.items);
      } else {
        setSinistros([]);
      }
    } catch (err) {
      setSinistros([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSinistros();
  }, []);

  const filteredSinistros = sinistros.filter(s => 
    ((s.clientes?.[0]?.nome || s.cliente?.nome || '')).toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.numero_sinistro || '').toString().includes(searchTerm) ||
    (s.informacoes || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.numeroDocumento || '').toString().includes(searchTerm)
  );

  return (
    <div className={`bg-white rounded-lg shadow-lg p-4 ${expanded ? 'h-[450px]' : 'h-auto'} flex flex-col ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          Sinistros
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold">
            {filteredSinistros.length}
          </span>
        </h2>
        <div className="flex gap-2">
          <button 
            onClick={() => setExpanded(!expanded)}
            className="p-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <svg 
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Conteúdo expansível */}
      {expanded && (
        <>
          {/* Campo de busca */}
          <div className="mb-3">
            <input
              type="text"
              placeholder="Buscar sinistros..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="space-y-3 flex-1 overflow-y-auto max-h-[280px]">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-500 text-sm">Carregando...</span>
          </div>
        ) : filteredSinistros.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="text-gray-400 text-4xl mb-2">🚨</div>
            <p className="text-gray-500 text-sm">
              {searchTerm ? 'Nenhum sinistro encontrado' : 'Nenhum sinistro cadastrado'}
            </p>
          </div>
        ) : (
          filteredSinistros.map((sinistro) => (
            <div 
              key={sinistro.id} 
              className="border border-gray-200 rounded-lg p-3 hover:bg-blue-50 hover:border-blue-200 transition-all duration-200 hover:shadow-sm"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-900">
                      Sinistro #{sinistro.numero_sinistro || sinistro.id}
                    </p>
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                      {sinistro.tipo || 'N/A'}
                    </span>
                  </div>
                  
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                      {sinistro.clientes?.[0]?.nome || sinistro.cliente?.nome || 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium">Documento:</span> {sinistro.numeroDocumento || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Status dos checkboxes */}
              <div className="flex gap-1 mb-2 flex-wrap">
                {[
                  { key: 'oficina', label: 'Oficina', color: 'bg-blue-100 text-blue-700' },
                  { key: 'abertura', label: 'Abertura', color: 'bg-yellow-100 text-yellow-700' },
                  { key: 'documentos', label: 'Documentos', color: 'bg-green-100 text-green-700' },
                  { key: 'vistoria_sinistro', label: 'Vistoria', color: 'bg-orange-100 text-orange-700' },
                  { key: 'analise', label: 'Análise', color: 'bg-purple-100 text-purple-700' },
                  { key: 'aprovacao', label: 'Aprovação', color: 'bg-indigo-100 text-indigo-700' },
                  { key: 'pagamento', label: 'Pagamento', color: 'bg-emerald-100 text-emerald-700' },
                  { key: 'concluido', label: 'Concluído', color: 'bg-gray-100 text-gray-700' }
                ].map(({ key, label, color }) => (
                  sinistro[key] && (
                    <span key={key} className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
                      {label}
                    </span>
                  )
                ))}
              </div>

              {expanded && sinistro.informacoes && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700">
                  <span className="font-medium">Informações:</span> {sinistro.informacoes}
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                <span className="flex items-center gap-1">
                  <span>📅</span>
                  {sinistro.data ? new Date(sinistro.data).toLocaleDateString('pt-BR') : 'Sem data'}
                </span>
                {sinistro.valor_franquia && (
                  <span className="text-green-600 font-medium">
                    Franquia: R$ {Number(sinistro.valor_franquia).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
          </div>
        </>
      )}
    </div>
  );
};

export default SinistroCard;
